import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Building2, Users, Trophy, TrendingUp, Award, Activity, Globe, MapPin, Loader2, Target, Crown, Bell, Wifi, WifiOff, RefreshCw, Sparkles, AlertCircle } from 'lucide-react';
import { federationApi, federationAlertsApi } from '../../lib/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from 'recharts';

const FederationDashboardTab = ({ sports = [] }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ sport: 'all', country: 'all' });
    const [alerts, setAlerts] = useState([]);
    const [wsStatus, setWsStatus] = useState('disconnected');
    const [scanLoading, setScanLoading] = useState(false);
    const wsRef = useRef(null);

    useEffect(() => { loadDashboard(); }, [filters]);

    // ========== ALERTS WEBSOCKET ==========
    useEffect(() => {
        connectAlertsWs();
        loadRecentAlerts();
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
        // eslint-disable-next-line
    }, []);

    const connectAlertsWs = () => {
        const backend = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        const ws = new WebSocket(`${backend}/api/federation-alerts/ws`);
        wsRef.current = ws;
        ws.onopen = () => setWsStatus('connected');
        ws.onmessage = (msg) => {
            try {
                const m = JSON.parse(msg.data);
                if (m.type === 'alert_history' && Array.isArray(m.data)) {
                    setAlerts((prev) => {
                        const ids = new Set(prev.map(a => a.id));
                        const merged = [...m.data.filter(a => !ids.has(a.id)), ...prev];
                        return merged.slice(0, 50);
                    });
                } else if (m.type === 'new_alert' && m.data) {
                    setAlerts((prev) => [m.data, ...prev].slice(0, 50));
                    if (m.data.severity === 'success' || m.data.severity === 'critical') {
                        toast(m.data.title, { description: m.data.message });
                    }
                }
            } catch (e) {}
        };
        ws.onclose = () => setWsStatus('disconnected');
        ws.onerror = () => setWsStatus('error');
    };

    const loadRecentAlerts = async () => {
        try {
            const res = await federationAlertsApi.getRecent({ limit: 30 });
            setAlerts(res.data.alerts || []);
        } catch (e) {}
    };

    const triggerScan = async () => {
        setScanLoading(true);
        try {
            const res = await federationAlertsApi.scan();
            toast.success(`Generated ${res.data.generated} new alerts`);
        } catch (e) {
            toast.error('Scan failed');
        } finally {
            setScanLoading(false);
        }
    };

    const triggerTestAlert = async () => {
        try {
            await federationAlertsApi.pushTest({
                severity: 'info',
                title: 'Test Alert',
                message: 'This is a federation alert test ping'
            });
        } catch (e) {
            toast.error('Test failed');
        }
    };

    const clearAlerts = async () => {
        if (!window.confirm('Clear all alerts?')) return;
        try {
            await federationAlertsApi.clear();
            setAlerts([]);
            toast.success('Alerts cleared');
        } catch (e) {
            toast.error('Failed');
        }
    };

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.sport !== 'all') params.sport = filters.sport;
            if (filters.country !== 'all') params.country = filters.country;
            const res = await federationApi.getDashboard(params);
            setData(res.data);
        } catch (e) {
            toast.error('Failed to load federation dashboard');
        } finally {
            setLoading(false);
        }
    };

    if (loading || !data) {
        return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;
    }

    const t = data.totals || {};
    const FUNNEL_COLORS = ['#06b6d4', '#22c55e', '#eab308', '#a855f7'];
    const TIER_COLORS = { Development: '#6b7280', Performance: '#06b6d4', Elite: '#eab308', 'High Performance': '#a855f7', Champion: '#ef4444' };
    const countries = ['Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Ethiopia', 'Ghana', 'Nigeria', 'South Africa'];

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 border border-cyan-500/30 rounded-lg p-5">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-cyan-500/20 rounded-lg">
                        <Building2 className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            Federation Intelligence
                            <span className="text-xs px-2 py-0.5 bg-cyan-500/30 text-cyan-200 rounded-full">NEW</span>
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Aggregated talent insights across the federation — pipeline funnel, sport performance, regional reach, and elite watchlist.
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
                <Select value={filters.sport} onValueChange={(v) => setFilters({...filters, sport: v})}>
                    <SelectTrigger className="w-[180px] bg-[#0d2847] border-white/10" data-testid="fed-sport-filter">
                        <SelectValue placeholder="Sport" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Sports</SelectItem>
                        {sports.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v})}>
                    <SelectTrigger className="w-[180px] bg-[#0d2847] border-white/10" data-testid="fed-country-filter">
                        <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Countries</SelectItem>
                        {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* KPI Tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {[
                    { label: 'Athletes', value: t.athletes, icon: Users, color: 'cyan' },
                    { label: 'Verified', value: t.verified_athletes, icon: Award, color: 'green' },
                    { label: 'Elite', value: t.elite_athletes, icon: Crown, color: 'yellow' },
                    { label: 'Tournaments', value: t.tournaments_total, icon: Trophy, color: 'purple' },
                    { label: 'Upcoming', value: t.tournaments_upcoming, icon: Target, color: 'blue' },
                    { label: 'Live Now', value: t.matches_live, icon: Activity, color: 'red' },
                    { label: 'Videos', value: t.video_analyses_completed, icon: TrendingUp, color: 'cyan' },
                    { label: 'Avg Score', value: t.avg_video_analysis_score, icon: Activity, color: 'green' },
                ].map((k, i) => (
                    <Card key={i} className="bg-[#0d2847] border-white/10 p-3">
                        <div className="flex items-center gap-2 mb-1">
                            <k.icon className={`w-4 h-4 text-${k.color}-400`} />
                            <span className="text-xs text-muted-foreground">{k.label}</span>
                        </div>
                        <div className={`text-xl font-bold text-${k.color}-400`} data-testid={`fed-kpi-${k.label.toLowerCase().replace(' ','-')}`}>
                            {k.value ?? 0}
                        </div>
                    </Card>
                ))}
            </div>

            {/* Real-Time Alerts Panel */}
            <Card className="bg-[#0d2847] border-white/10 p-5" data-testid="federation-alerts-panel">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="font-semibold flex items-center gap-2">
                        <Bell className="w-5 h-5 text-yellow-400" />
                        Real-Time Federation Alerts
                        {wsStatus === 'connected'
                            ? <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full flex items-center gap-1"><Wifi className="w-3 h-3" /> Live</span>
                            : <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full flex items-center gap-1"><WifiOff className="w-3 h-3" /> Offline</span>
                        }
                        <span className="text-xs text-muted-foreground ml-2">({alerts.length} alerts)</span>
                    </h3>
                    <div className="flex gap-2">
                        <Button onClick={triggerScan} size="sm" variant="outline" className="border-white/10" disabled={scanLoading} data-testid="alerts-scan-btn">
                            {scanLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                            Scan
                        </Button>
                        <Button onClick={triggerTestAlert} size="sm" variant="outline" className="border-white/10" data-testid="alerts-test-btn">
                            <Bell className="w-4 h-4 mr-1" />Test
                        </Button>
                        <Button onClick={clearAlerts} size="sm" variant="outline" className="border-red-500/30 text-red-400" data-testid="alerts-clear-btn">
                            Clear
                        </Button>
                    </div>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="alerts-feed">
                    {alerts.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                            <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-30" />
                            No alerts yet — click <strong>Scan</strong> to detect tier-ups, top rankings, and verifications.
                        </div>
                    ) : alerts.map((a) => (
                        <AlertItem key={a.id} alert={a} />
                    ))}
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Talent Funnel */}
                <Card className="bg-[#0d2847] border-white/10 p-5">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5 text-cyan-400" /> Talent Pipeline Funnel
                    </h3>
                    <div className="space-y-3">
                        {(data.funnel || []).map((stage, i) => (
                            <div key={stage.stage} data-testid={`funnel-${stage.stage.toLowerCase().replace(' ','-')}`}>
                                <div className="flex justify-between text-sm mb-1">
                                    <span>{stage.stage}</span>
                                    <span className="font-bold">{stage.count} <span className="text-xs text-muted-foreground">({stage.percent}%)</span></span>
                                </div>
                                <div className="h-3 rounded-full bg-[#0a1628] overflow-hidden">
                                    <div className="h-full transition-all" style={{ width: `${stage.percent}%`, background: FUNNEL_COLORS[i] }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Tier Distribution */}
                <Card className="bg-[#0d2847] border-white/10 p-5">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Crown className="w-5 h-5 text-yellow-400" /> Tier Distribution
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie data={data.tier_distribution} dataKey="count" nameKey="tier" cx="50%" cy="50%" outerRadius={80} label>
                                {data.tier_distribution.map((entry, i) => (
                                    <Cell key={i} fill={TIER_COLORS[entry.tier] || '#888'} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={{ background: '#0a1628', border: '1px solid #ffffff20', color: '#fff' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </Card>
            </div>

            {/* Sport Matrix */}
            <Card className="bg-[#0d2847] border-white/10 p-5">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-400" /> Sport Performance Matrix
                </h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="fed-sport-matrix">
                        <thead className="bg-[#0a1628] border-b border-white/10">
                            <tr>
                                <th className="text-left px-3 py-2 font-semibold">Sport</th>
                                <th className="text-right px-3 py-2">Athletes</th>
                                <th className="text-right px-3 py-2">Verified</th>
                                <th className="text-right px-3 py-2">Elite</th>
                                <th className="text-right px-3 py-2">Avg ELO</th>
                                <th className="text-right px-3 py-2">Win Rate</th>
                                <th className="text-right px-3 py-2">Verification</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.sport_matrix || []).slice(0, 15).map((s) => (
                                <tr key={s.sport} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="px-3 py-2 font-medium">{s.sport}</td>
                                    <td className="px-3 py-2 text-right">{s.athlete_count}</td>
                                    <td className="px-3 py-2 text-right text-green-400">{s.verified_count}</td>
                                    <td className="px-3 py-2 text-right text-yellow-400">{s.elite_count}</td>
                                    <td className="px-3 py-2 text-right">{s.avg_elo}</td>
                                    <td className="px-3 py-2 text-right">{s.win_rate}%</td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <div className="w-16 h-1.5 bg-[#0a1628] rounded">
                                                <div className="h-full bg-cyan-400 rounded" style={{ width: `${s.verification_rate}%` }} />
                                            </div>
                                            <span className="text-xs">{s.verification_rate}%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Regional */}
                <Card className="bg-[#0d2847] border-white/10 p-5">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-cyan-400" /> By Country
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data.regional?.by_country?.slice(0, 8) || []} layout="vertical">
                            <XAxis type="number" stroke="#666" />
                            <YAxis type="category" dataKey="name" stroke="#aaa" width={100} />
                            <Tooltip contentStyle={{ background: '#0a1628', border: '1px solid #ffffff20', color: '#fff' }} />
                            <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                {/* Top Performers */}
                <Card className="bg-[#0d2847] border-white/10 p-5">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Crown className="w-5 h-5 text-yellow-400" /> Elite Watchlist (Top 10 by ELO)
                    </h3>
                    <div className="space-y-2">
                        {(data.top_performers || []).map((p, i) => (
                            <div key={p.id} className="flex items-center justify-between p-2 hover:bg-white/5 rounded">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold w-6 text-center text-yellow-400">#{i + 1}</span>
                                    <div>
                                        <div className="text-sm font-medium">{p.name}</div>
                                        <div className="text-xs text-muted-foreground">{p.sport} · {p.country}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{p.elo}</div>
                                    <div className="text-xs text-purple-400">{p.tier}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="text-xs text-muted-foreground text-right">
                Generated: {new Date(data.generated_at).toLocaleString()}
            </div>
        </div>
    );
};

const AlertItem = ({ alert }) => {
    const severityStyles = {
        info: { bg: 'bg-blue-500/10', border: 'border-blue-500/40', text: 'text-blue-400' },
        success: { bg: 'bg-green-500/10', border: 'border-green-500/40', text: 'text-green-400' },
        warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/40', text: 'text-yellow-400' },
        critical: { bg: 'bg-red-500/10', border: 'border-red-500/40', text: 'text-red-400' },
    };
    const s = severityStyles[alert.severity] || severityStyles.info;
    const categoryIcons = {
        level_up: Crown,
        ranking_change: TrendingUp,
        milestone: Award,
        verification: Award,
        system: Activity,
    };
    const Icon = categoryIcons[alert.category] || Bell;
    return (
        <div className={`p-3 rounded border-l-4 ${s.bg} ${s.border}`} data-testid={`alert-${alert.severity}`}>
            <div className="flex items-start gap-3">
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.text}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{alert.title}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${s.bg} ${s.text}`}>{alert.severity}</span>
                        {alert.sport && <span className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded">{alert.sport}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{alert.message}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(alert.timestamp).toLocaleString()}</div>
                </div>
            </div>
        </div>
    );
};

export default FederationDashboardTab;